// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The SEND ENVELOPE — subject, from-name, reply-to, Mailchimp audience id.
//
// Why this is on the review surface at all: the frozen artifact is the HTML BODY ONLY. The
// envelope is read from edge-function env at send time (reporting-newsletter-send/index.ts:87-91)
// and is NOT part of artifact_sha256. A subject line is part of a financial promotion, so
// reviewing the body while the subject stays unreviewed and mutable is exactly the review-A-
// send-B failure this control exists to prevent.
//
// 0003i's fix: the render stage pins the envelope into the freeze manifest, the review row stamps
// envelope_sha256 FROM that manifest (never from this client), and the deliver stage recomputes
// the digest from live env and refuses on drift. So this card is not decoration — it is half of
// what the reviewer is attesting to.
//
// NOT-FOUND DISCIPLINE (same rule as CellValue): a missing envelope is an explicit sentinel that
// says the send is blocked. It is NEVER rendered as a blank field or an em-dash that could read
// as "fine".

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Mail, ShieldX } from "lucide-react";
import type { NewsletterSendEnvelope } from "@/hooks/investment-reporting/useNewsletterReview";
import { envelopeFieldLabel, shortHash } from "./labels";

const FIELDS = ["subject", "from_name", "reply_to", "audience_id"] as const;

export function SendEnvelopeCard({
  envelope,
  envelopeSha256,
  className,
}: {
  envelope: NewsletterSendEnvelope | null | undefined;
  envelopeSha256: string | null | undefined;
  className?: string;
}) {
  const pinned = !!envelope && !!envelopeSha256;

  return (
    <Card className={cn("p-4 space-y-3", className)}>
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        <div className="font-heading text-sm font-medium text-foreground">Send envelope</div>
      </div>

      {!pinned ? (
        <div className="flex items-start gap-1.5">
          <ShieldX className="h-3.5 w-3.5 mt-0.5 shrink-0 text-destructive" aria-hidden />
          <p className="font-body text-xs text-muted-foreground">
            Not found — this frozen artifact pins no send envelope. The subject line, from-name,
            reply-to and audience are read from edge-function env at send time and are not bound to
            these bytes, so a review could not cover the whole promotion. The render stage must
            record <code className="font-mono">manifest.send_envelope_sha256</code> before a review
            can be recorded.
          </p>
        </div>
      ) : (
        <>
          <dl className="space-y-1.5">
            {FIELDS.map((f) => {
              const raw = envelope?.[f];
              const value = typeof raw === "string" && raw.length > 0 ? raw : null;
              return (
                <div key={f} className="grid grid-cols-[7.5rem_1fr] gap-2 items-start">
                  <dt className="font-body text-[11px] text-muted-foreground pt-0.5">
                    {envelopeFieldLabel(f)}
                  </dt>
                  <dd className="font-body text-sm text-foreground break-words">
                    {value ?? (
                      <span className="text-xs italic text-muted-foreground">
                        Not found — the manifest pins no {envelopeFieldLabel(f).toLowerCase()}
                      </span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
          <p className="font-body text-[11px] text-muted-foreground break-all">
            envelope sha256 {shortHash(envelopeSha256, 16)} · recomputed from live env at send; a
            secret edited after this review invalidates the review rather than changing the promotion.
          </p>
        </>
      )}
    </Card>
  );
}
